import { useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Avatar,
  Stack,
  IconButton
} from "@mui/material";
import { styled } from "@mui/system";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import VerifiedIcon from "@mui/icons-material/Verified";
import FavoriteIcon from "@mui/icons-material/Favorite";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import TwitterIcon from "@mui/icons-material/Twitter";
import FacebookIcon from "@mui/icons-material/Facebook";
import DownloadIcon from "@mui/icons-material/Download";
import LoadingButton from "@mui/lab/LoadingButton";
import doctorImage from "../../../../assets/images/aboutpagephoto.jpeg";
import MaleProfilePhoto from "../../../../assets/images/male-doc.png";
import FemaleProfilePhoto from "../../../../assets/images/female-doc.webp";

import TitleSectionComponent from "../../../../components/core-components/titleSection";
import { useNavigate } from "react-router-dom";
import { AboutService } from "../../../../network/AboutService";
import { useQuery } from "@tanstack/react-query";
import { MemberService } from "../../../../network/MemberService";
import FoundingMembers from "./components/FoundingMembersSection";

const IconBox = styled(Box)(({ theme }) => ({
  backgroundColor: "var(--red-primary)",
  borderRadius: "50%",
  padding: theme.spacing(1),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#fff"
}));

const StyledCardContent = styled(CardContent)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  padding: "16px !important"
});

const DoctorCard = styled(Card)(({ theme }) => ({
  textAlign: "center",
  padding: theme.spacing(2),
  borderRadius: "16px",
  boxShadow: "0px 4px 4px 4px rgba(0, 0, 0, 0.2)",
  transition: "transform 0.3s, box-shadow 0.3s",
  height: "400px",
  width: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  "&:hover": {
    transform: "scale(1.05)",
    boxShadow: "0px 4px 4px 4px rgba(0, 0, 0, 0.2)"
  }
}));

const About = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const handleDownload = async () => {
    setLoading(true);
    setTimeout(() => {
      const link = document.createElement("a");
      link.href = "/tuzuk.docx";
      link.download = "besiktasli-doktorlar-dernegi-tuzugu.docx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setLoading(false);
    }, 2000);
  };

  const aboutService = new AboutService();

  const { data } = useQuery({
    queryKey: ["about"],
    queryFn: async () => {
      const res = await aboutService.getAll();
      return res?.data;
    }
  });

  const memberService = new MemberService();

  const { data: boardsManagementmembersList } = useQuery({
    queryKey: ["boardsManagementmembersList"],
    queryFn: async () => {
      const res = await memberService.getAll({
        management: true
      });
      return res?.data;
    }
  });

  return (
    <section
      id='about'
      style={{
        backgroundColor: "var(--bg-color)",
        height: "100%",
        paddingBottom: "100px"
      }}>
      <div className='container'>
        <TitleSectionComponent titleText='Hakkımızda' />

        <Grid container spacing={4} alignItems='center' pt={2}>
          <Grid item xs={12} md={4}>
            <img
              src={doctorImage}
              alt='Doctor'
              style={{
                height: "600px",
                objectFit: "cover",
                width: "100%",
                borderRadius: "16px"
              }}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <Grid container>
              <Grid item xs={12} sm={6}>
                <Card
                  elevation={0}
                  sx={{ height: "100%", backgroundColor: "transparent" }}>
                  <CardContent>
                    <Stack spacing={2} alignItems='flex-start'>
                      <IconBox>
                        <MedicalServicesIcon />
                      </IconBox>
                      <span className='H-24'>Derneğimiz</span>

                      <span
                        className='B-18'
                        style={{ color: "var(--gray-primary)" }}>
                        {data?.kurulusumuz}
                      </span>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Card
                  elevation={0}
                  sx={{ height: "100%", backgroundColor: "transparent" }}>
                  <CardContent>
                    <Stack spacing={2} alignItems='flex-start'>
                      <IconBox>
                        <FavoriteIcon />
                      </IconBox>
                      <span className='H-24'>Vizyonumuz</span>

                      <span
                        className='B-18'
                        style={{ color: "var(--gray-primary)" }}>
                        {data?.vizyonumuz}
                      </span>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              <Grid
                item
                xs={12}
                container
                justifyContent='flex-end'
                alignItems='center'
                spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Card elevation={0} sx={{ backgroundColor: "transparent" }}>
                    <CardContent>
                      <Stack spacing={2} alignItems='flex-start'>
                        <IconBox>
                          <VerifiedIcon />
                        </IconBox>
                        <span className='H-24'>Kurucu Hekimlerimiz</span>
                        <span
                          className='B-18'
                          style={{ color: "var(--gray-primary)" }}>
                          {data?.kurucuHekimlerimiz}
                        </span>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <LoadingButton
                    variant='contained'
                    loading={loading}
                    onClick={handleDownload}
                    loadingIndicator='İndiriliyor...'
                    startIcon={<DownloadIcon />}
                    sx={{
                      width: "100%",
                      backgroundColor: "#333",
                      color: "white",
                      borderRadius: "20px",
                      padding: "5px 20px",
                      height: "50px",
                      textTransform: "capitalize",
                      "&:hover": { backgroundColor: "#000" }
                    }}>
                    <span className='B-18' style={{ color: "#fff" }}>
                      Tüzüğümüzü İndirin
                    </span>
                  </LoadingButton>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <TitleSectionComponent mt={10} titleText='Yönetim Kurulumuz' />

        <Grid container spacing={4}>
          {boardsManagementmembersList?.map((member: any, index: number) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <DoctorCard
                onClick={() =>
                  navigate(
                    `/uyeler/${member._id}/${member.name}-${member.surname}`
                  )
                }>
                <Avatar
                  alt={member.name}
                  src={
                    member.image ||
                    (member.gender?.toLowerCase() === "male"
                      ? MaleProfilePhoto
                      : FemaleProfilePhoto)
                  }
                  sx={{ width: 150, height: 150, margin: "20px auto 0" }}
                />
                <StyledCardContent>
                  <Stack spacing={1} alignItems='center'>
                    <span className='H-24'>
                      {member.title} {member.name} {member.surname}
                    </span>
                    <span className='B-18'>{member.role}</span>
                  </Stack>
                  <Box>
                    {member.socialMedia?.linkedin && (
                      <IconButton
                        component='a'
                        href={member.socialMedia.linkedin}
                        target='_blank'
                        rel='noopener noreferrer'>
                        <LinkedInIcon />
                      </IconButton>
                    )}
                    {member.socialMedia?.twitter && (
                      <IconButton
                        component='a'
                        href={member.socialMedia.twitter}
                        target='_blank'
                        rel='noopener noreferrer'>
                        <TwitterIcon />
                      </IconButton>
                    )}
                    {member.socialMedia?.facebook && (
                      <IconButton
                        component='a'
                        href={member.socialMedia.facebook}
                        target='_blank'
                        rel='noopener noreferrer'>
                        <FacebookIcon />
                      </IconButton>
                    )}
                  </Box>
                </StyledCardContent>
              </DoctorCard>
            </Grid>
          ))}
        </Grid>
        <FoundingMembers />
      </div>
    </section>
  );
};

export default About;
